import {
  BreakpointObserver,
  Breakpoints,
  MediaMatcher,
} from '@angular/cdk/layout';
import { DestroyRef, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { shareReplayCold } from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  isMobile$: Observable<boolean>; // max-width: 599px and orientation: portrait, max-width: 959px and orientation: landscape
  isTablet$: Observable<boolean>; // min-width: 600px and max-width: 839px and orientation: portrait, min-width: 960px and max-width: 1279px and orientation: landscape
  isDesktop$: Observable<boolean>; // min-width: 840px and orientation: portrait, min-width: 1280px and orientation: landscape
  minWidthLargeDesktop$: Observable<boolean>; // 1280px and up
  /**
   * True when the primary input is imprecise, such as a finger on a touchscreen.
   * Use this rather than viewport width to adapt interactions to touch, because
   * a narrow or zoomed desktop window is still driven by a mouse.
   */
  hasCoarsePointer$: Observable<boolean>;

  constructor(private _breakpointObserver: BreakpointObserver) {
    this.isMobile$ = this._breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(
        map((result) => result.matches),
        shareReplayCold()
      );
    this.isTablet$ = this._breakpointObserver
      .observe([Breakpoints.Tablet])
      .pipe(
        map((result) => result.matches),
        shareReplayCold()
      );
    this.isDesktop$ = this._breakpointObserver
      .observe([Breakpoints.WebLandscape])
      .pipe(
        map((result) => result.matches),
        shareReplayCold()
      );
    this.minWidthLargeDesktop$ = this._breakpointObserver
      .observe(['(min-width: 1280px)'])
      .pipe(
        map((result) => result.matches),
        shareReplayCold()
      );
    this.hasCoarsePointer$ = this._breakpointObserver
      .observe(['(pointer: coarse)'])
      .pipe(
        map((result) => result.matches),
        shareReplayCold()
      );
  }
}

export class MediaBreakpoint {
  private _mediaQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  isActive = signal(false);
  isActive$ = toObservable(this.isActive);

  constructor(media: MediaMatcher, destroyRef: DestroyRef, breakpoint: string) {
    this._mediaQuery = media.matchMedia(`(max-width: ${breakpoint})`);
    this.isActive.set(this._mediaQuery.matches);
    this._mobileQueryListener = () =>
      this.isActive.set(this._mediaQuery.matches);
    this._mediaQuery.addEventListener('change', this._mobileQueryListener);

    destroyRef.onDestroy(() =>
      this._mediaQuery.removeEventListener('change', this._mobileQueryListener)
    );
  }
}
